import { DefaultProjectKey, RecordType } from '@wiztime/core';
import { queries, type Store } from '@wiztime/storage';
import { SyncState, type SyncStatus } from '@wiztime/sync';
import type { TFunction } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../hooks/useServices';
import { useSyncStatus } from '../../hooks/useSyncStatus';
import { writeStorage } from '../../lib/safeStorage';
import { FLAG_ON, StorageKey } from '../../lib/storageKeys';
import { Button, ButtonVariant } from '../../ui/Button';
import styles from './Welcome.module.css';

/** What the first sync of a device that joined an existing account is doing. */
export const FirstSyncPhase = {
  Waiting: 'waiting',
  Done: 'done',
  Failed: 'failed',
} as const;
export type FirstSyncPhase = (typeof FirstSyncPhase)[keyof typeof FirstSyncPhase];

const FAILED_STATES: readonly SyncState[] = [SyncState.Error, SyncState.Offline, SyncState.SpaceMissing];

export function firstSyncPhase(status: SyncStatus): FirstSyncPhase {
  if (FAILED_STATES.includes(status.state)) return FirstSyncPhase.Failed;
  if (status.lastSyncAt !== null && status.state === SyncState.Idle) return FirstSyncPhase.Done;
  return FirstSyncPhase.Waiting;
}

/**
 * Whether the first sync brought the user's data, i.e. the first-run screen was done on another
 * device: projects or settings exist, or the store grew beyond what it held before signing in.
 * (The default projects are created on each device and never synchronised, so an account that only
 * recorded blocks on its default projects shows up as the growth.)
 */
async function hasSyncedSetup(store: Store, recordsBefore: number): Promise<boolean> {
  const projects = await store.repo.listByType(RecordType.Project);
  if (projects.length > 0) return true;
  const settings = await store.repo.listByType(RecordType.Setting);
  if (settings.length > 0) return true;
  return (await queries.allRecords(store.repo)).length > recordsBefore;
}

function defaultNames(t: TFunction) {
  return {
    [DefaultProjectKey.Work]: t('defaultProject.work'),
    [DefaultProjectKey.Sleep]: t('defaultProject.sleep'),
    [DefaultProjectKey.Chores]: t('defaultProject.chores'),
  };
}

interface FirstSyncPendingProps {
  /** Records this device held before signing in. */
  readonly recordsBefore: number;
  /** Leave the waiting screen and show the app. */
  readonly onDone: () => void;
}

/**
 * "Syncing data…" after logging in to an existing account. When the first sync finishes with
 * projects or settings, the first-run screen is skipped; on failure the user can retry or go on.
 */
export function FirstSyncPending({ recordsBefore, onDone }: FirstSyncPendingProps) {
  const { t } = useTranslation();
  const { store, sync } = useServices();
  const status = useSyncStatus();
  const phase = firstSyncPhase(status);
  const [retrying, setRetrying] = useState(false);
  const finished = useRef(false);

  useEffect(() => {
    if (phase !== FirstSyncPhase.Done || finished.current) return;
    finished.current = true;
    void hasSyncedSetup(store, recordsBefore)
      .then(async (synced) => {
        if (!synced) return;
        // The first-run screen creates the default projects of this device; skipping it must not lose them.
        await store.seedDefaults(defaultNames(t));
        writeStorage(StorageKey.Onboarded, FLAG_ON);
      })
      .catch((error: unknown) => console.error('could not read the synced data', error))
      .finally(onDone);
  }, [phase, store, recordsBefore, onDone, t]);

  // The account may already hold settings (day start, sleep range) that this device has not pulled.
  // The first-run screen would write them with a fresh timestamp and overwrite them on every device,
  // so skipping only seeds the defaults (zero timestamp: pulled values win) and marks the screen done.
  const skip = (): void => {
    if (finished.current) return;
    finished.current = true;
    void store
      .seedDefaults(defaultNames(t))
      .then(() => writeStorage(StorageKey.Onboarded, FLAG_ON))
      .catch((error: unknown) => console.error('could not seed the defaults', error))
      .finally(onDone);
  };

  const retry = (): void => {
    setRetrying(true);
    void sync
      .manager()
      .then((manager) => manager.syncNow())
      .catch((error: unknown) => console.error('first sync retry failed', error))
      .finally(() => setRetrying(false));
  };

  const failed = phase === FirstSyncPhase.Failed && !retrying;
  return (
    <main className={styles.page}>
      <div className={styles.column}>
        <div className={styles.card + ' ' + styles.pending} data-testid="first-sync-pending" data-phase={phase}>
          {failed ? (
            <>
              <p className={styles.error} role="alert" data-testid="first-sync-error">
                {t('welcome.firstSync.failed')}
              </p>
              <div className={styles.actions}>
                <Button variant={ButtonVariant.Primary} onClick={retry} data-testid="first-sync-retry">
                  {t('welcome.firstSync.retry')}
                </Button>
                <Button onClick={skip} data-testid="first-sync-skip">
                  {t('welcome.firstSync.skip')}
                </Button>
              </div>
            </>
          ) : (
            <div role="status">
              <progress aria-label={t('welcome.firstSync.pending')} />
              <p>{t('welcome.firstSync.pending')}</p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
